import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Divider, Dropdown, Loading, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { EIPBreadcrumb } from '@/pages/eip/components/EIPBreadcrumb'

import {
  BILLING_PROTOCOL_OPTIONS,
  fetchBillingModelOptions,
  fetchBillingRouteOptions,
  fetchProjectBillingRecords,
} from '../api'
import { isPresetKey, presetRange, TimeRangeFilter, TimeRangeValue } from './TimeRangeFilter'

const PAGE_SIZE = 10
const ALL = 'all'

export function RouteBillingDetailPage() {
  const { t } = useTranslation('modelRoute')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const rangeParam = searchParams.get('range')
  const initialPreset = isPresetKey(rangeParam) ? rangeParam : null

  const [route, setRoute] = useState<string>(searchParams.get('route') || ALL)
  const [model, setModel] = useState<string>(ALL)
  const [routeType, setRouteType] = useState<string>(ALL)
  const [timeRange, setTimeRange] = useState<TimeRangeValue | null>(() => {
    if (!initialPreset) return null
    const [from, to] = presetRange(initialPreset)
    return { from: from.toISOString(), to: to.toISOString() }
  })
  const [page, setPage] = useState(1)

  const { data: routeOptions } = useQuery({
    queryKey: ['billingRouteOptions'],
    queryFn: fetchBillingRouteOptions,
  })

  const { data: modelOptions } = useQuery({
    queryKey: ['billingModelOptions'],
    queryFn: fetchBillingModelOptions,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['projectBillingRecords', route, model, routeType, timeRange, page],
    queryFn: () =>
      fetchProjectBillingRecords({
        route: route === ALL ? undefined : route,
        model: model === ALL ? undefined : model,
        routeType: routeType === ALL ? undefined : routeType,
        from: timeRange?.from,
        to: timeRange?.to,
        page,
        pageSize: PAGE_SIZE,
      }),
  })

  const records = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex items-center gap-3 h-[60px]">
        <button
          type="button"
          className="text-tertiary cursor-pointer hover:text-primary"
          onClick={() => navigate('/model-route')}
          aria-label="back"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <EIPBreadcrumb
          items={[
            {
              label: (
                <Trans ns="modelRoute" i18nKey="title">
                  Model Route
                </Trans>
              ),
              to: '/model-route',
            },
            {
              label: t('billingDetail.pageTitle', 'Billing Detail'),
              to: '/model-route/billing',
              active: true,
            },
          ]}
        />
      </div>

      <Divider />

      <div className="flex justify-between gap-8 my-4">
        <div className="flex gap-8">
          <div className="flex flex-col gap-2">
            <div className="text-secondary t-sb">
              <Trans ns="modelRoute" i18nKey="billingDetail.filterRoute">
                Route
              </Trans>
            </div>
            <Dropdown
              rootClassName="t-sb w-60"
              className="h-11"
              showAllOption
              options={(routeOptions ?? []).map((r) => ({
                label: r.label,
                value: r.value,
                className: 't-sm',
              }))}
              value={route}
              onChange={(value) => {
                setRoute(String(value))
                setPage(1)
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-secondary t-sb">
              <Trans ns="modelRoute" i18nKey="billingDetail.filterModel">
                Model
              </Trans>
            </div>
            <Dropdown
              rootClassName="t-sb w-60"
              className="h-11"
              showAllOption
              options={(modelOptions ?? []).map((m) => ({
                label: m.label,
                value: m.value,
                className: 't-sm',
              }))}
              value={model}
              onChange={(value) => {
                setModel(String(value))
                setPage(1)
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-secondary t-sb">
              <Trans ns="modelRoute" i18nKey="billingDetail.filterRouteType">
                Route Type
              </Trans>
            </div>
            <Dropdown
              rootClassName="t-sb w-60"
              className="h-11"
              showAllOption
              // TODO(model-route-api): the design wants service types (MaaS/Instance/…)
              // here but the API filter is protocol-only
              options={BILLING_PROTOCOL_OPTIONS.map((rt) => ({
                label: rt.label,
                value: rt.value,
                className: 't-sm',
              }))}
              value={routeType}
              onChange={(value) => {
                setRouteType(String(value))
                setPage(1)
              }}
            />
          </div>
        </div>
        <TimeRangeFilter
          initialPreset={initialPreset}
          onChange={(range) => {
            setTimeRange(range)
            setPage(1)
          }}
        />
      </div>

      {isLoading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!isLoading && records.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
      {!isLoading && records.length > 0 && (
        <table className="table mt-4 table-fixed">
          <thead>
            <tr>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerRoute">
                  Route
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerModel">
                  Model
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerRouteType">
                  Route Type
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerToken">
                  Token
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerLatency">
                  Latency / First Token
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerCost">
                  Cost
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="billingDetail.headerTime">
                  Time
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => (
              <tr key={index}>
                <td className="t-sm t-sb">{record.route}</td>
                <td className="t-sm t-sb">{record.model}</td>
                <td className="t-sm">{record.routeType}</td>
                <td className="t-sm">
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="billingDetail.tokenIn">
                      In
                    </Trans>
                  </span>{' '}
                  <span className="t-sb">{record.tokenIn.toLocaleString('en-US')}</span>{' '}
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="billingDetail.tokenOut">
                      Out
                    </Trans>
                  </span>{' '}
                  <span className="t-sb">{record.tokenOut.toLocaleString('en-US')}</span>
                </td>
                <td className="t-sm">
                  <span className="t-sb">{record.latencySec}</span>
                  <span className="text-secondary">s</span>
                  {' / '}
                  <span className="t-sb">{record.firstTokenSec}</span>
                  <span className="text-secondary">s</span>
                </td>
                <td className="t-sm">
                  <span className="t-sb">{record.cost}</span>{' '}
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="table.credits">
                      Credits
                    </Trans>
                  </span>
                </td>
                <td className="t-sm text-tertiary">{record.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {total > 0 && !isLoading && (
        <Pagination
          size={PAGE_SIZE}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={setPage}
        />
      )}
    </div>
  )
}
